/**
 * Service pour l'impression, l'export et l'envoi par email du ticket de refoulement
 */

import { RefoulementTicketData } from '@/types/refoulementTicket';
import { emailPDF } from './pdfService';
import { generateRefoulementPrintHTML } from '@/utils/refoulementTicketUtils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export interface RefoulementTicketServiceOptions {
  onSuccess?: (action: string) => void;
  onError?: (error: string) => void;
}

class RefoulementTicketService {
  private static instance: RefoulementTicketService;

  private constructor() {}

  public static getInstance(): RefoulementTicketService {
    if (!RefoulementTicketService.instance) {
      RefoulementTicketService.instance = new RefoulementTicketService();
    }
    return RefoulementTicketService.instance;
  }

  /**
   * Imprime le ticket de refoulement (utilise HTML comme le ticket normal)
   */
  async printTicket(
    ticketData: RefoulementTicketData,
    options: RefoulementTicketServiceOptions = {}
  ): Promise<void> {
    try {
      // Générer le HTML pour l'impression
      const printHTML = generateRefoulementPrintHTML(ticketData);
      
      // Ouvrir une nouvelle fenêtre pour l'impression
      let printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes,toolbar=no,menubar=no');
      
      if (!printWindow) {
        // Méthode alternative avec iframe
        await this.printTicketAlternative(ticketData, options);
        return;
      }

      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
      
      options.onSuccess?.('impression');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'impression';
      console.error('❌ Erreur lors de l\'impression du ticket de refoulement:', error);
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Méthode alternative d'impression avec iframe
   */
  private async printTicketAlternative(
    ticketData: RefoulementTicketData,
    options: RefoulementTicketServiceOptions = {}
  ): Promise<void> {
    try {
      const printHTML = generateRefoulementPrintHTML(ticketData);
      
      // Créer un iframe caché pour l'impression
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      iframe.style.width = '210mm';
      iframe.style.height = '297mm';
      iframe.style.border = 'none';
      
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(printHTML);
        iframeDoc.close();
        
        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          
          // Nettoyer après impression
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        };
      }
      
      options.onSuccess?.('impression');
    } catch (error) {
      console.error('❌ Erreur lors de l\'impression alternative:', error);
      options.onError?.(error instanceof Error ? error.message : 'Erreur lors de l\'impression');
      throw error;
    }
  }

  /**
   * Exporte le ticket de refoulement en PDF (utilise HTML comme le ticket normal)
   */
  async exportTicketPDF(
    ticketData: RefoulementTicketData,
    options: RefoulementTicketServiceOptions = {}
  ): Promise<void> {
    try {
      // Générer le HTML
      const printHTML = generateRefoulementPrintHTML(ticketData);
      
      // Créer un élément temporaire pour contenir le HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = printHTML;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '210mm';
      tempDiv.style.backgroundColor = 'white';
      
      document.body.appendChild(tempDiv);
      
      // Attendre que les images se chargent
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Convertir le HTML en canvas
      const canvas = await html2canvas(tempDiv, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: tempDiv.scrollHeight,
        logging: false
      });
      
      // Créer le PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Télécharger
      const filename = `refoulement_${ticketData.numero_refoulement}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
      
      // Nettoyer
      document.body.removeChild(tempDiv);
      
      options.onSuccess?.('export');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'export PDF';
      console.error('❌ Erreur lors de l\'export PDF du ticket de refoulement:', error);
      options.onError?.(errorMessage);
      throw error;
    }
  }

  /**
   * Envoie le ticket de refoulement par email (utilise HTML comme le ticket normal)
   */
  async sendTicketEmail(
    ticketData: RefoulementTicketData,
    _recipients: string,
    _subject?: string,
    _message?: string,
    _cc?: string,
    _bcc?: string,
    options: RefoulementTicketServiceOptions = {}
  ): Promise<void> {
    try {
      // Générer le HTML
      const printHTML = generateRefoulementPrintHTML(ticketData);
      
      // Créer un élément temporaire pour contenir le HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = printHTML;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '210mm';
      tempDiv.style.backgroundColor = 'white';
      
      document.body.appendChild(tempDiv);
      
      // Attendre que les images se chargent
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Convertir le HTML en canvas
      const canvas = await html2canvas(tempDiv, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 794,
        height: tempDiv.scrollHeight,
        logging: false
      });
      
      // Créer le PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Envoyer par email
      const filename = `refoulement_${ticketData.numero_refoulement}_${new Date().toISOString().split('T')[0]}.pdf`;
      emailPDF(pdf, filename);
      
      // Nettoyer
      document.body.removeChild(tempDiv);
      
      options.onSuccess?.('email');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de l\'envoi par email';
      console.error('❌ Erreur lors de l\'envoi par email du ticket de refoulement:', error);
      options.onError?.(errorMessage);
      throw error;
    }
  }


}

export default RefoulementTicketService.getInstance();
